import React from 'react'

import { breakpoint } from 'app/theme/tokens'

/**
 * Returns true when the viewport is below the `tablet` breakpoint (<600px).
 *
 * Built on window.matchMedia rather than a window-resize listener so it
 * reflects the actual layout decision the CSS would make. Subscribes to
 * `change` events so it re-renders when the user rotates / resizes.
 *
 *   const isMobile = useIsMobile()
 *   if (isMobile) return <MobileShell>...</MobileShell>
 *
 * For other breakpoints, pass a key:
 *   useIsBelow('desktop')  → true when <960px
 */
const getMatch = (query) =>
  typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia(query).matches
    : false

export const useIsBelow = (key = 'tablet') => {
  const query = `(max-width: ${
    { mobile: 599, tablet: 599, desktop: 959, wide: 1279 }[key] ?? 599
  }px)`

  const [matches, setMatches] = React.useState(() => getMatch(query))

  React.useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const mql = window.matchMedia(query)
    const handler = (e) => setMatches(e.matches)
    // Older Safari uses addListener; newer browsers use addEventListener.
    if (mql.addEventListener) mql.addEventListener('change', handler)
    else mql.addListener(handler)
    setMatches(mql.matches)
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler)
      else mql.removeListener(handler)
    }
  }, [query])

  return matches
}

export const useIsMobile = () => useIsBelow('tablet')

// Eslint-friendly: re-export the breakpoint helpers so callers don't need a
// second import if they want raw values too.
export { breakpoint }
